'use client'

import { ApolloError } from '@apollo/client'

import { deleteCookie, getCookie, setCookie } from 'cookies-next'
import {
  createContext,
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import useCurrentLocale from '../../core/hook/useCurrentLocale'
import { useMutation } from '../../core/hook/useMutation'
import { createCustomerDocument } from '../../queries/CreateCustomer.gql'
import { generateCustomerTokenDocument } from '../../queries/GenerateCustomerToken.gql'
import { useLazyQuery } from '../../core/hook/useLazyQuery'
import { CustomerDocument } from '../../queries/Customer.gql'
import { md5Encrypt } from '../utils/gtmHelpers'

type RegisterFormType = {
  newsletter: boolean
  terms?: boolean
  birthDate: string
  firstName: string
  lastName: string
  email: string
  emailConfirmation: string
  password: string
  passwordConfirmation?: string
  title?: string | undefined
  wechatId?: string
}

export type UserContextType = {
  hydrated: boolean
  userToken: string | null | undefined
  login: (
    email: string,
    password: string,
    recaptchaToken?: string,
    forceLogout?: boolean,
  ) => Promise<{
    success: boolean
    userToken: string | null
    error?: string
    customer: Customer | null
  }>
  registration: (
    userToRegister: RegisterFormType,

    recaptchaToken?: string,
  ) => Promise<{ success: boolean; error?: string }>
  logout: () => void
  setUserCookie: (token: string) => void
  isLogged: boolean
  user: Customer | null
  isLoadingUser: boolean
  reloadUserInformation: () => Promise<Customer | null>
}

export const UserContext = createContext<UserContextType>({
  hydrated: false,
  userToken: null,
  login: () =>
    Promise.resolve({ success: false, userToken: null, customer: null as Customer | null }),
  registration: () => Promise.resolve({ success: false }),
  logout: () => {},
  setUserCookie: () => {},
  isLogged: false,
  isLoadingUser: true,
  user: null,
  reloadUserInformation: async () => { return null as Customer | null },
})

const UserProvider: FunctionComponent<{ children: ReactNode }> = ({ children }) => {
  const [GenerateCustomerToken] = useMutation(generateCustomerTokenDocument)
  const [CreateCustomer] = useMutation(createCustomerDocument)
  const currentLocale = useCurrentLocale()
  const [userToken, setUserToken] = useState<string | null>(
    String(
      getCookie('userToken', {
        path: `/${currentLocale}`,
      }),
    ),
  )
  const [user, setUser] = useState<Customer | null>(null)
  const [loadingCustomer, setLoadingCustomer] = useState(false)
  const [execCustomerQuery, { loading }] = useLazyQuery(CustomerDocument, {
    fetchPolicy: 'no-cache',
  })

  const hydrated = useMemo(() => {
    if (!userToken) {
      return true
    }
    return Boolean(user)
  }, [userToken, user])

  useEffect(() => {
    const userTokenCookie = getCookie('userToken')
    const token = userTokenCookie ? String(userTokenCookie) : null
    setUserToken(token)
  }, [])

  const logout = useCallback(() => {
    setUserToken(null)
    setUser(null)
    deleteCookie('userToken', {
      path: '/' + currentLocale,
    })
    deleteCookie('userEmailEncrypted', {
      path: '/' + currentLocale,
    })
  }, [])

  const login = useCallback(
    async (email: string, password: string, captchaToken?: string, forceLogout?: boolean) =>
      GenerateCustomerToken({
        variables: {
          email,
          password,
        },
        context: {
          headers: {
            Store: currentLocale,
            'X-ReCaptcha': captchaToken || '',
          },
        },
      })
        .then(async (response) => {
          if (response.data?.generateCustomerToken) {
            if (forceLogout) {
              logout()
            }
            const token = response.data?.generateCustomerToken.token as string
            setUserCookie(token)
            let userTokenCookie = getCookie('userToken', {
              path: `/${currentLocale}`,
            })
            while (!userTokenCookie) {
              await new Promise((resolve) => setTimeout(resolve, 10))
              userTokenCookie = getCookie('userToken', {
                path: `/${currentLocale}`,
              })
            }
            return { userToken: token, customer: (await loadUser()) ?? null }
          }

          throw Error(response.errors && response.errors[0] && response.errors[0].message)
        })
        .then(({ userToken, customer }) => {
          return {
            success: true,
            userToken,
            customer,
          }
        })
        .catch((e: ApolloError) => ({
          success: false,
          error: e.message,
          userToken: null,
          customer: null,
        })),
    [GenerateCustomerToken, logout],
  )

  const registration = useCallback(
    (userToRegister: RegisterFormType, captchaToken?: string) =>
      CreateCustomer({
        variables: {
          email: userToRegister.email,
          prefix: userToRegister.title,
          password: userToRegister.password,
          firstName: userToRegister.firstName,
          lastName: userToRegister.lastName,
          dateOfBirth: userToRegister.birthDate,
          isSubscribed: userToRegister.newsletter,
          wechatId: userToRegister.wechatId,
        },
        context: {
          headers: {
            Store: currentLocale,
            'X-ReCaptcha': captchaToken || '',
          },
        },
      })
        .then(() => ({
          success: true,
        }))
        .catch((error: ApolloError) => ({
          success: false,
          error: error.message,
        })),
    [],
  )

  const setUserCookie = (token: string) => {
    const expiresAt = new Date()
    expiresAt.setSeconds(
      expiresAt.getSeconds() + Number(process.env.NEXT_PUBLIC_USER_TOKEN_DURATION),
    )
    setCookie('userToken', token, {
      expires: expiresAt,
      path: '/' + currentLocale,
    })
    setUserToken(token)
  }

  const loadUser = useCallback(async () => {
    const userToken = getCookie('userToken', {
      path: `/${currentLocale}`,
    })
    if (!userToken) {
      logout()
      return null
    }
    setLoadingCustomer(true)
    const customer = await execCustomerQuery({
      context: {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
        userToken,
      },
    })
    if (!customer.data?.customer) {
      throw new Error('Failed to load user')
    }
    setCookie('userEmailEncrypted', customer.data.customer.email, {
      path: '/' + currentLocale,
    })
    setCookie('customerIdEncrypted', md5Encrypt(customer.data.customer.customer_id), {
      path: '/' + currentLocale,
    })
    setUser((customer.data?.customer ?? null) as Customer | null)
    setLoadingCustomer(false)
    return (customer.data.customer as Customer) ?? null
  }, [execCustomerQuery])

  useEffect(() => {
    if (userToken && !loadingCustomer && !user) {
      loadUser()
    }
  }, [userToken, loadingCustomer])

  const context = useMemo(
    () => ({
      hydrated,
      userToken,
      login,
      registration,
      logout,
      isLogged: Boolean(userToken),
      user,
      isLoadingUser: loading,
      reloadUserInformation: loadUser,
      setUserCookie: setUserCookie,
    }),
    [userToken, login, registration, logout, user, loading, loadUser, hydrated, setUserCookie],
  )

  return <UserContext.Provider value={context}>{children}</UserContext.Provider>
}

export default UserProvider
